import {
  COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING,
  CollapsibleScreenHeader,
} from "@/components/prooftree/CollapsibleScreenHeader"
import { EmptyState } from "@/components/prooftree/EmptyState"
import { PressableScale } from "@/components/prooftree/PressableScale"
import { SkillChip } from "@/components/prooftree/SkillChip"
import { Avatar, AvatarFallbackText } from "@/components/ui/avatar"
import { Box } from "@/components/ui/box"
import { HStack } from "@/components/ui/hstack"
import { Text } from "@/components/ui/text"
import { VStack } from "@/components/ui/vstack"
import { compactFilterChipStyle } from "@/constants/ChipStyles"
import { Colors } from "@/constants/Colors"
import { useAuth } from "@/contexts/AuthContext"
import { getSkillDomainColor, groupFeed, type FeedItem } from "@/data/prooftree"
import {
  addEqGrowthCircleComment,
  addEqGrowthCircleReaction,
  listEqGrowthCirclePosts,
} from "@/services/eqParityBackend"
import { router } from "expo-router"
import {
  Heart,
  Lock,
  MessageCircle,
  PlayCircle,
  Send,
  ShieldCheck,
  UsersRound,
} from "lucide-react-native"
import { useEffect, useRef, useState } from "react"
import { Animated, Platform, TouchableOpacity } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"

export default function GroupFeedScreen() {
  const auth = useAuth()
  const isSignedIn = Boolean(auth.session)
  const [feedItems, setFeedItems] = useState<FeedItem[]>(isSignedIn ? [] : groupFeed)
  const [feedMode, setFeedMode] = useState<"supabase" | "local">("local")
  const [feedMessage, setFeedMessage] = useState(
    isSignedIn
      ? "Loading your Supabase Growth Circle posts."
      : "Using local fallback Growth Circle posts until Supabase sync is available.",
  )
  const [savingActionId, setSavingActionId] = useState<string | null>(null)
  const scrollY = useRef(new Animated.Value(0)).current
  const visibleFeedItems = isSignedIn && feedMode !== "supabase" ? [] : feedItems

  useEffect(() => {
    let cancelled = false

    async function loadFeed() {
      const result = await listEqGrowthCirclePosts()
      if (cancelled) return

      setFeedMode(result.mode)
      setFeedMessage(result.message)
      setFeedItems(result.mode === "supabase" || isSignedIn ? result.items : groupFeed)
    }

    loadFeed()

    return () => {
      cancelled = true
    }
  }, [auth.session?.access_token, isSignedIn])

  async function updateFeedCount(
    itemId: string,
    key: "likes" | "comments",
    writeAction: () => Promise<{ message: string; persisted?: boolean }>,
  ) {
    if (savingActionId) return

    setSavingActionId(`${itemId}-${key}`)
    const result = await writeAction()
    if (!isSignedIn || result.persisted) {
      setFeedItems((current) =>
        current.map((item) => (item.id === itemId ? { ...item, [key]: item[key] + 1 } : item)),
      )
    }
    setFeedMessage(result.message)
    setSavingActionId(null)
  }

  async function reactToPost(item: FeedItem) {
    await updateFeedCount(item.id, "likes", () =>
      addEqGrowthCircleReaction({ postId: item.id, reaction: "well_coached" }),
    )
  }

  async function commentOnPost(item: FeedItem) {
    await updateFeedCount(item.id, "comments", () =>
      addEqGrowthCircleComment({
        postId: item.id,
        body: "Thanks for sharing this growth moment.",
      }),
    )
  }

  return (
    <SafeAreaView
      className="flex-1"
      edges={["top"]}
      style={{ backgroundColor: Colors.dark.surface }}
    >
      <CollapsibleScreenHeader
        scrollY={scrollY}
        eyebrow="Growth Circle"
        title="Group Feed"
        subtitle="Shared achievements from your private circle. Supabase posts load when signed in."
        accentColor={Colors.dark.growth}
        backFallbackHref="/profile"
        statusText={feedMode === "supabase" ? "Synced" : "Local"}
      />
      <Animated.ScrollView
        className="flex-1"
        contentContainerStyle={{
          padding: 20,
          paddingTop: COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING,
          paddingBottom: Platform.OS === "web" ? 72 : 48,
        }}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], {
          useNativeDriver: false,
        })}
      >
        <Box
          className="mb-5 rounded-3xl border p-4"
          style={{
            backgroundColor: `${Colors.dark.growth}1A`,
            borderColor: `${Colors.dark.growth}47`,
          }}
        >
          <HStack className="items-start gap-3">
            <Box
              className="h-11 w-11 items-center justify-center rounded-2xl"
              style={{ backgroundColor: `${Colors.dark.growth}2E` }}
            >
              <Lock color={Colors.dark.growth} size={19} />
            </Box>
            <VStack className="flex-1 gap-1">
              <Text className="font-bold text-white">Private by default</Text>
              <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
                Reflections stay private unless you share them to the circle. {feedMessage}
              </Text>
            </VStack>
          </HStack>
        </Box>

        {visibleFeedItems.length > 0 ? (
          <VStack className="gap-4">
            {visibleFeedItems.map((item) => {
              const color = getSkillDomainColor(item.skillIds[0] ?? "active_listening_presence")
              return (
                <PressableScale key={item.id}>
                  <Box
                    className="rounded-3xl border p-5"
                    style={{
                      backgroundColor: Colors.dark.surfaceSecondary,
                      borderColor: `${color}44`,
                    }}
                  >
                    <HStack className="mb-4 items-start gap-3">
                      <Avatar className="border" style={{ borderColor: color }}>
                        <AvatarFallbackText>{item.person}</AvatarFallbackText>
                      </Avatar>
                      <VStack className="flex-1 gap-1">
                        <HStack className="items-center justify-between gap-2">
                          <Text className="font-bold text-white">{item.person}</Text>
                          <Text className="text-xs" style={{ color: Colors.dark.textSecondary }}>
                            {item.date}
                          </Text>
                        </HStack>
                        <HStack className="items-center gap-1.5">
                          <UsersRound color={color} size={13} />
                          <Text className="text-xs font-bold" style={{ color }}>
                            {item.role}
                          </Text>
                        </HStack>
                      </VStack>
                    </HStack>

                    <HStack className="mb-3 flex-wrap items-center gap-2">
                      {item.skillIds.map((skillId) => (
                        <SkillChip key={skillId} skillId={skillId} compact />
                      ))}
                    </HStack>

                    <Text className="mb-2 text-xl font-bold text-white">{item.title}</Text>
                    <Text className="mb-4 leading-5" style={{ color: Colors.dark.textSecondary }}>
                      {item.summary}
                    </Text>

                    {item.videoThumbnail ? (
                      <TouchableOpacity activeOpacity={0.86}>
                        <Box
                          className="mb-4 rounded-2xl border p-3"
                          style={{
                            backgroundColor: `${color}14`,
                            borderColor: `${color}44`,
                          }}
                        >
                          <HStack className="items-center gap-3">
                            <Box
                              className="h-14 w-14 items-center justify-center rounded-xl"
                              style={{ backgroundColor: `${color}24` }}
                            >
                              <PlayCircle color={color} size={28} />
                            </Box>
                            <VStack className="flex-1">
                              <Text className="font-bold text-white">{item.videoThumbnail}</Text>
                              <Text
                                className="text-xs"
                                style={{ color: Colors.dark.textSecondary }}
                              >
                                Video proof preview
                              </Text>
                            </VStack>
                          </HStack>
                        </Box>
                      </TouchableOpacity>
                    ) : null}

                    <HStack className="items-center justify-between">
                      <HStack className="items-center gap-2">
                        <GrowthCircleAction
                          icon={Heart}
                          label={`Well coached · ${item.likes}`}
                          color={color}
                          disabled={savingActionId === `${item.id}-likes`}
                          onPress={() => reactToPost(item)}
                        />
                        <GrowthCircleAction
                          icon={MessageCircle}
                          label={`${item.comments}`}
                          color={color}
                          disabled={savingActionId === `${item.id}-comments`}
                          onPress={() => commentOnPost(item)}
                        />
                      </HStack>
                      <Box
                        className="rounded-full border px-3 py-2"
                        style={{
                          ...compactFilterChipStyle,
                          backgroundColor: `${Colors.dark.proof}14`,
                          borderColor: `${Colors.dark.proof}38`,
                        }}
                      >
                        <HStack className="items-center gap-2">
                          <ShieldCheck color={Colors.dark.proof} size={14} />
                          <Text className="text-xs font-bold" style={{ color: Colors.dark.proof }}>
                            {feedMode === "supabase" ? "Supabase shared" : "Fallback shared"}
                          </Text>
                        </HStack>
                      </Box>
                    </HStack>
                  </Box>
                </PressableScale>
              )
            })}
          </VStack>
        ) : (
          <EmptyState
            icon={UsersRound}
            title="No circle posts yet"
            description="Share a Journal entry to create the first Supabase-backed Growth Circle post, or sign in to sync shared posts."
            actionLabel="Open Journal"
            onAction={() => router.push("/journal")}
          />
        )}

        <TouchableOpacity activeOpacity={0.86} onPress={() => router.push("/journal")}>
          <Box
            className="mt-5 rounded-2xl px-4 py-4"
            style={{ backgroundColor: Colors.dark.primary }}
          >
            <HStack className="items-center justify-center gap-2">
              <Send color={Colors.dark.primaryForeground} size={18} />
              <Text className="font-bold" style={{ color: Colors.dark.primaryForeground }}>
                Choose a Journal Entry to Share
              </Text>
            </HStack>
          </Box>
        </TouchableOpacity>
      </Animated.ScrollView>
    </SafeAreaView>
  )
}

function GrowthCircleAction({
  icon: Icon,
  label,
  color,
  disabled,
  onPress,
}: {
  icon: typeof Heart
  label: string
  color: string
  disabled: boolean
  onPress: () => void
}) {
  return (
    <TouchableOpacity activeOpacity={0.86} disabled={disabled} onPress={onPress}>
      <Box
        className="rounded-full border px-3 py-2"
        style={{
          ...compactFilterChipStyle,
          backgroundColor: `${color}12`,
          borderColor: `${color}33`,
          opacity: disabled ? 0.64 : 1,
        }}
      >
        <HStack className="items-center gap-1.5">
          <Icon color={color} size={15} />
          <Text className="text-xs font-bold" style={{ color }}>
            {label}
          </Text>
        </HStack>
      </Box>
    </TouchableOpacity>
  )
}
